// CW Full Squad — org-wide directory (every person, one row).
//
// Filters at the top; each row is inline-editable by managers+ (job title,
// notes, role) via `PATCH /workspace/full-squad/{id}`.  Admins can rewrite
// roles; managers can maintain job titles + notes.
//
// The IO menu at top-right lets any admin export the roster as CSV
// (for hand-editing in Excel) and re-import it later.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Search, RefreshCw, Crown, Palmtree, Save, X } from "lucide-react";
import { workspaceApi, teamColor } from "./_shared";
import SquadAvatar from "../../components/squad/SquadAvatar";
import { useSquadFaces } from "./panels/TeamCard";
import { useAuth } from "../../context/AuthContext";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import { useProgressiveList } from "../../hooks/useProgressiveList";
import "./workspace.css";

export default function FullSquad() {
  const { user } = useAuth();
  const isManager = !!(user?.system_admin || user?.is_admin
    || ["admin", "owner", "manager", "system_admin"].includes((user?.role || "").toLowerCase())
    || ["owner", "manager", "system_admin"].includes((user?.role_workspace || "").toLowerCase()));
  const isAdmin = !!(user?.system_admin || user?.is_admin
    || ["admin", "owner", "system_admin"].includes((user?.role || "").toLowerCase()));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const faces = useSquadFaces();

  const load = async () => {
    setLoading(true);
    try { setData(await workspaceApi.fullSquad()); }
    catch { toast.error("Failed to load Full Squad"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const users = data?.users || [];
  const teams = data?.teams || [];

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return users.filter((u) => {
      if (qq && !`${u.name} ${u.email} ${u.job_title || ""}`.toLowerCase().includes(qq)) return false;
      if (teamFilter && !u.teams.some((t) => t.id === teamFilter)) return false;
      if (roleFilter) {
        const r = (u.role || u.role_workspace || "").toLowerCase();
        if (roleFilter === "manager"  && !["admin", "owner", "manager", "system_admin"].includes(r)) return false;
        if (roleFilter === "staff"    &&  ["admin", "owner", "manager", "system_admin"].includes(r)) return false;
        if (roleFilter === "orphan"   && u.teams.length > 0) return false;
      }
      return true;
    });
  }, [users, q, teamFilter, roleFilter]);

  const { visibleCount, showMore } = useProgressiveList(`${q}|${teamFilter}|${roleFilter}`, 50);
  const shownPeople = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  return (
    <div className="space-y-4" data-testid="workspace-full-squad">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Users className="w-4 h-4 text-emerald-300" />
        <div className="text-[13px] font-semibold text-white">CW Full Squad</div>
        <LiveCount shown={shownPeople.length} total={filtered.length} noun="person" testId="ws-squad-live-count" />
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="ws-input pl-8" placeholder="Search name / email / title…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="ws-squad-search" />
        </div>
        <select className="ws-input max-w-[180px]" value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          data-testid="ws-squad-team-filter">
          <option value="">All teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="ws-input max-w-[140px]" value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="ws-squad-role-filter">
          <option value="">Any role</option>
          <option value="manager">Managers</option>
          <option value="staff">Staff</option>
          <option value="orphan">No team</option>
        </select>
        <button onClick={load} className="text-slate-400 hover:text-white p-1.5" title="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Grid */}
      <div className="ws-card p-0 overflow-hidden" data-testid="ws-squad-table">
        <div className="grid grid-cols-[1fr_130px_1fr_130px_100px_60px] gap-3 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/5 sticky top-0 bg-white/[0.02]">
          <div>Name / Title</div>
          <div>Role</div>
          <div>Teams</div>
          <div>Line manager</div>
          <div className="text-right">Load</div>
          <div className="text-right">Edit</div>
        </div>
        {loading && <div className="text-center text-slate-500 py-8">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-slate-500 py-8 text-[13px]">
            No people match those filters.
          </div>
        )}
        {shownPeople.map((u) => {
          const line = users.find((x) => x.id === u.reports_to);
          return (
            <div key={u.id}
              className="grid grid-cols-[1fr_130px_1fr_130px_100px_60px] gap-3 px-3 py-2 border-b border-white/[0.03] items-center text-[12px] hover:bg-white/[0.02]"
              data-testid={`ws-squad-row-${u.id}`}
            >
              <div className="min-w-0 flex items-center gap-2.5">
                <SquadAvatar name={u.name} url={(faces[u.id] || faces[u.name] || {}).avatar_url} status={(faces[u.id] || faces[u.name] || {}).status} size={30} />
                <div className="min-w-0">
                <div className="text-slate-200 font-medium truncate flex items-center gap-1.5">
                  {u.teams.some(t => t.is_manager) && <Crown className="w-3 h-3 text-amber-300 shrink-0" />}
                  {u.out_of_office && <Palmtree className="w-3 h-3 text-rose-300 shrink-0" />}
                  {u.name}
                </div>
                <div className="text-[10.5px] font-mono text-slate-500 truncate">
                  {u.job_title || <span className="text-slate-600 italic">no title set</span>}
                </div>
                </div>
              </div>
              <div className="text-[10.5px] font-mono text-slate-400 uppercase tracking-widest truncate">
                {u.role_workspace || u.role || "—"}
              </div>
              <div className="flex flex-wrap gap-1 min-w-0">
                {u.teams.length === 0
                  ? <span className="text-[10.5px] text-slate-600 italic">—</span>
                  : u.teams.map(t => { const c = teamColor(t); return (
                    <span key={t.id} title={teams.find((x) => x.id === t.id)?.description || t.name}
                      className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-full border"
                      style={{ borderColor: `${c}55`, background: `${c}18`, color: c }}>
                      {t.name}
                      {t.is_manager && <Crown className="w-2.5 h-2.5 text-amber-300" />}
                    </span>
                  ); })
                }
              </div>
              <div className="text-[11px] text-slate-300 truncate" title={line?.name}>
                {line?.name || <span className="text-slate-600">—</span>}
              </div>
              <div className="text-right text-[11px] tabular-nums">
                <span className={u.workload.open >= 8 ? "text-amber-300" : "text-slate-300"}>{u.workload.open}</span>
                {u.workload.overdue > 0 && <span className="text-rose-300 ml-1">/ {u.workload.overdue}</span>}
              </div>
              <div className="text-right">
                {isManager && (
                  <button onClick={() => setEditing(u)}
                    className="px-1.5 py-0.5 rounded border border-white/10 hover:bg-white/5 text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-white"
                    data-testid={`ws-squad-edit-${u.id}`}
                  >Edit</button>
                )}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length > 0 && (
          <InfiniteSentinel
            onReach={showMore}
            hasMore={shownPeople.length < filtered.length}
            loading={false}
            count={shownPeople.length}
            total={filtered.length}
            testId="ws-squad-sentinel"
          />
        )}
      </div>

      {editing && (
        <EditRowPopover user={editing} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={load} />
      )}

      {/* Admin-only tip */}
      {isAdmin && (
        <div className="text-[11px] text-slate-500 italic">
          Tip: prefer to hand-edit in Excel? Export the full-team CSV from <b>Admin → Export</b>, tweak, then re-upload via <b>Admin → Import</b>.
        </div>
      )}
    </div>
  );
}

function EditRowPopover({ user, isAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({
    job_title: user.job_title || "",
    notes:     user.notes || "",
    role:      user.role || "",
    role_workspace: user.role_workspace || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        job_title: form.job_title,
        notes:     form.notes,
        ...(isAdmin ? { role: form.role, role_workspace: form.role_workspace } : {}),
      };
      await workspaceApi.patchUser(user.id, payload);
      toast.success("Saved");
      onSaved?.(); onClose?.();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="ws-squad-edit-popover">
      <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#0F1420] p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Edit person</div>
            <div className="text-lg font-semibold text-white">{user.name}</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Job title</div>
          <input className="ws-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            data-testid="ws-squad-edit-title" placeholder="e.g. Senior BDR · Sales Manager · Analyst" />
        </div>
        <div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Notes</div>
          <textarea rows={3} className="ws-input" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="ws-squad-edit-notes"
            placeholder="e.g. Covers Marta while she's on leave · Prefers async" />
        </div>
        {isAdmin && (
          <>
            <div>
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Role (auth)</div>
              <select className="ws-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="ws-squad-edit-role">
                <option value="user">user</option>
                <option value="manager">manager</option>
                <option value="admin">admin</option>
                <option value="owner">owner</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">Workspace role</div>
              <select className="ws-input" value={form.role_workspace}
                onChange={(e) => setForm({ ...form, role_workspace: e.target.value })}
                data-testid="ws-squad-edit-role-ws">
                <option value="">— none —</option>
                <option value="manager">manager</option>
                <option value="owner">owner</option>
                <option value="system_admin">system_admin</option>
              </select>
            </div>
          </>
        )}
        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 font-mono uppercase tracking-widest text-[10.5px] hover:bg-amber-500/20 flex items-center gap-1"
            data-testid="ws-squad-edit-save"
          ><Save className="w-3 h-3" /> Save</button>
          <button onClick={onClose} className="text-[11px] font-mono text-slate-400 hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}
